import type { StageName } from '../lib/collections';

export type InputType = 'freeText' | 'textArea' | 'multipleChoice' | 'slider' | 'emojiSlider';

export interface StageQuestion {
  id: string; // deterministic UUID e.g. "s1_q1"
  prompt: string;
  type: InputType;
  placeholder?: string;
  required: boolean;
  options?: string[]; // for multipleChoice
  emojiStops?: { emoji: string; label: string }[]; // for emojiSlider
  sliderMin?: number;
  sliderMax?: number;
  sliderMinLabel?: string;
  sliderMaxLabel?: string;
  defaultValue?: string;
}

export interface StageConfig {
  key: StageName;
  number: number; // 1-6 (introduction is 0)
  name: string;
  emoji: string;
  timeEstimate: string;
  questions: StageQuestion[];
  hasHomework: boolean;
  homeworkComponent?: string; // component name
  worldlyInsight: {
    title: string;
    emoji: string;
    text: string;
  };
  familyHomework: string;
  tokenReward: number;
  nextStage: StageName | 'completed';
}

export const STAGE_CONFIGS: Record<string, StageConfig> = {
  ideaExplosion: {
    key: 'ideaExplosion',
    number: 1,
    name: 'The OG Origin Story',
    emoji: '🌟',
    timeEstimate: '~15 min',
    questions: [
      {
        id: 's1_q1',
        prompt: "Pretend you're telling your best friend about your idea. In 2-3 words, what do you say?",
        type: 'freeText',
        placeholder: 'Snack vending app, Cute dino stickers...',
        required: true,
      },
      {
        id: 's1_q2',
        prompt: 'What does your idea actually do?',
        type: 'textArea',
        placeholder: 'It helps you make your own dino stickers and share them with friends.',
        required: false,
      },
      {
        id: 's1_q3',
        prompt: 'How hyped would your friends be about this idea?',
        type: 'emojiSlider',
        required: false,
        defaultValue: '2',
        emojiStops: [
          { emoji: '😏', label: 'Meh' },
          { emoji: '😎', label: 'Cool' },
          { emoji: '🤩', label: 'Awesome' },
          { emoji: '🤯', label: 'Mind Blown' },
          { emoji: '💀', label: 'Legend' },
        ],
      },
    ],
    hasHomework: true,
    homeworkComponent: 'LogoBuilder',
    worldlyInsight: {
      title: 'Idea Magic',
      emoji: '💡',
      text: "Did you know Instagram started as Burbn? The founders realized people only used one feature — photo sharing — so they pivoted. Sometimes the best ideas come from watching what people actually do with your product.",
    },
    familyHomework:
      "Share your business idea with your family and ask: 'What problems do you think this could solve that I haven't thought of?'",
    tokenReward: 30,
    nextStage: 'problemSolver',
  },
  problemSolver: {
    key: 'problemSolver',
    number: 2,
    name: 'The Struggle Is Real',
    emoji: '🔍',
    timeEstimate: '~20 min',
    questions: [
      {
        id: 's2_q1',
        prompt: "Describe the problem your product or service solves in one sentence — like you're venting to a friend.",
        type: 'textArea',
        placeholder: "Ugh! It's so annoying when...",
        required: false,
      },
      {
        id: 's2_q2',
        prompt: 'How annoying is this problem?',
        type: 'multipleChoice',
        required: false,
        options: ['😐 Just a little', '😤 Pretty frustrating', '🤯 Drives me nuts', '💀 Worst thing ever'],
      },
      {
        id: 's2_q3',
        prompt: 'When does this problem hit hardest?',
        type: 'multipleChoice',
        required: false,
        options: ['At school', 'At home', 'With friends', 'Online', 'Everywhere'],
      },
      {
        id: 's2_q4',
        prompt: 'If your problem were a villain in a movie/book, what would its name be?',
        type: 'freeText',
        placeholder: 'The Great Dino Sticker Shortage Shadow',
        required: false,
      },
    ],
    hasHomework: true,
    homeworkComponent: 'ProblemDiary',
    worldlyInsight: {
      title: 'Problem-solving superstars',
      emoji: '🔍',
      text: "Sara Blakely cut the feet off her pantyhose because she couldn't find the right undergarment — and that \"problem\" became Spanx, a billion-dollar company!",
    },
    familyHomework:
      "Tonight, ask each family member: 'What's the most annoying problem you deal with every week?'",
    tokenReward: 30,
    nextStage: 'customerDetective',
  },
  customerDetective: {
    key: 'customerDetective',
    number: 3,
    name: "From 'Ugh!' To 'Aha!'",
    emoji: '🕵️',
    timeEstimate: '~15 min',
    questions: [
      {
        id: 's3_q1',
        prompt: 'If your product were a superhero, what would it be named?',
        type: 'freeText',
        placeholder: 'The Sticker Witch',
        required: false,
      },
      {
        id: 's3_q2',
        prompt: 'Write your solution as if it\'s a tagline to the superhero.',
        type: 'textArea',
        placeholder: 'She comes, she sticks, she slays!',
        required: false,
      },
    ],
    hasHomework: true,
    homeworkComponent: 'CeoContract',
    worldlyInsight: {
      title: 'Customer insight magic',
      emoji: '🕵️',
      text: "The creators of Pokemon originally thought only boys would like it, but they discovered girls loved it too! This doubled their customer base overnight.",
    },
    familyHomework:
      "Ask your family members to pretend they're your ideal customer. Have them tell you: 'If I saw this product, what would make me want to buy it immediately?'",
    tokenReward: 30,
    nextStage: 'mvpMadness',
  },
  mvpMadness: {
    key: 'mvpMadness',
    number: 4,
    name: 'Who Will Buy This?',
    emoji: '🛠️',
    timeEstimate: '~20 min',
    questions: [
      {
        id: 's4_q1',
        prompt: "Who's your #1 customer?",
        type: 'multipleChoice',
        required: false,
        options: ['Kids', 'Parents', 'Teachers', 'Others (tell us!)'],
      },
      {
        id: 's4_q2',
        prompt: 'Give your ideal customer a name + 3 emojis that describe them.',
        type: 'freeText',
        placeholder: 'Arjun 🦖🦕📔',
        required: false,
      },
      {
        id: 's4_q3',
        prompt: "Where do they hear about new cool stuff, like what you're building?",
        type: 'multipleChoice',
        required: false,
        options: ['YouTube / Games', 'Friends', 'Parents or teachers', 'Ads / Stores'],
      },
      {
        id: 's4_q4',
        prompt: 'Say your ideal customer saw your product on a shelf, or online, what would their first reaction be? (5 words max)',
        type: 'freeText',
        placeholder: 'OMG I need this now!',
        required: false,
      },
    ],
    hasHomework: true,
    homeworkComponent: 'FriendSurvey',
    worldlyInsight: {
      title: 'MVP success story',
      emoji: '🛠️',
      text: "The first iPhone didn't have copy/paste, couldn't record video, and had no app store! But it was still revolutionary because it did the most important things really well.",
    },
    familyHomework:
      "Show your family your prototype idea and ask: 'What's the one thing this product should do better than anything else?'",
    tokenReward: 30,
    nextStage: 'moneyMoves',
  },
  moneyMoves: {
    key: 'moneyMoves',
    number: 5,
    name: 'Show Me The Money!!',
    emoji: '💰',
    timeEstimate: '~20 min',
    questions: [
      {
        id: 's5_q1',
        prompt: 'How much would people pay for it?',
        type: 'multipleChoice',
        required: false,
        options: ['A < ₹50', 'B ₹50–₹200', 'C ₹200–₹500', 'D ₹500+'],
      },
      {
        id: 's5_q2',
        prompt: 'What do you need to make it real?',
        type: 'multipleChoice',
        required: false,
        options: ['Materials', 'Time', 'Helpers', 'Technology'],
      },
      {
        id: 's5_q3',
        prompt: 'Would you rather sell...',
        type: 'slider',
        required: false,
        sliderMin: 0,
        sliderMax: 100,
        sliderMinLabel: 'Few items at high price',
        sliderMaxLabel: 'Lots of items at low price',
        defaultValue: '50',
      },
      {
        id: 's5_q4',
        prompt: "What's your money move?",
        type: 'multipleChoice',
        required: false,
        options: [
          'Keep it cheap to get fans',
          'Charge premium — flex value',
          'Offer versions at different prices',
          'Free trial, then upgrade',
        ],
      },
    ],
    hasHomework: true,
    homeworkComponent: 'WouldYouRather',
    worldlyInsight: {
      title: 'Pricing psychology',
      emoji: '💰',
      text: "Did you know that prices ending in 9 (like ₹19 or ₹29) make people think they're getting a better deal, even though it's only ₹1 less?",
    },
    familyHomework:
      "Ask your family: 'When you're deciding whether to buy something, what makes you think the price is worth it?'",
    tokenReward: 30,
    nextStage: 'competitionStation',
  },
  competitionStation: {
    key: 'competitionStation',
    number: 6,
    name: 'The Game Plan',
    emoji: '📊',
    timeEstimate: '~15 min',
    questions: [
      {
        id: 's6_q1',
        prompt: "What's your next move?",
        type: 'multipleChoice',
        required: false,
        options: [
          'Build a prototype',
          'Get customers to show interest',
          'Find a teammate',
          'Pitch it to someone big',
        ],
      },
      {
        id: 's6_q2',
        prompt: "Imagine it's 1 year later… what's happening?",
        type: 'textArea',
        placeholder: '100 people have new cool dino stickers now',
        required: false,
      },
      {
        id: 's6_q3',
        prompt: 'If someone gave you ₹1000 to help your idea… what would you spend it on?',
        type: 'multipleChoice',
        required: false,
        options: ['Materials', 'App design', 'Stickers / packaging', 'Ads', 'Paying a helper'],
      },
    ],
    hasHomework: true,
    homeworkComponent: 'ElevatorPitch',
    worldlyInsight: {
      title: 'Competition wisdom',
      emoji: '📊',
      text: 'When Netflix started, everyone said "Why would anyone want movies by mail when Blockbuster exists?" But Netflix focused on convenience and eventually put Blockbuster out of business!',
    },
    familyHomework:
      "Ask your family: 'When you choose between similar products, what makes you pick one over another?'",
    tokenReward: 30,
    nextStage: 'completed',
  },
};

// Ordered list of playable stages (excludes introduction)
export const STAGE_ORDER: StageName[] = [
  'ideaExplosion',
  'problemSolver',
  'customerDetective',
  'mvpMadness',
  'moneyMoves',
  'competitionStation',
];

export function getStageConfig(stageKey: string): StageConfig | undefined {
  return STAGE_CONFIGS[stageKey];
}

export function getStageByNumber(n: number): StageConfig | undefined {
  return STAGE_ORDER[n - 1] ? STAGE_CONFIGS[STAGE_ORDER[n - 1]] : undefined;
}
